import { addHours, differenceInHours, startOfHour, subHours } from 'date-fns';
import {
  WeatherStationHistoricalData,
  WeatherStationHistoricalDataCell,
  WeatherStationObservation,
  WeatherStationObservationDataKey,
} from 'features/weatherStations/weatherStations.types';
import { formatDate } from 'shared/dates';
import { SENSOR_TYPE_TO_COLOR_STOPS } from 'features/weatherStations/weatherStations.constants';
import { getTableCellSx } from 'features/weatherStations/components/WeatherStationDataTable/WeatherStationDataTable.styles';
import { FocusedSensorType } from 'state/useWeatherStationState';
import { getInterpolatedColor } from 'features/mapping/mapping.utils';
import { EMPTY_DATA_VALUE } from '../../constants';
import {
  getObservationSensorTypeGroups,
  OBSERVATION_SENSOR_TYPE_TO_LABEL,
  OBSERVATIONS_DATA_PARSERS,
} from './WeatherStationDetails.constants';

export const getLatestObservation = (
  observations: WeatherStationObservation[],
): WeatherStationObservation | null => {
  if (observations.length === 0) return null;

  // Sort observations by timestamp (newest first)
  const sortedObs = [...observations].sort(
    (a, b) => b.timestamp.getTime() - a.timestamp.getTime(),
  );

  return sortedObs[0];
};

/**
 * Generates an array of bucket START times between the given start and end dates,
 * incremented by the specified step in hours.
 *
 * For example, with start=10:00, end=13:00, step=1:
 * Returns [10:00, 11:00, 12:00] representing buckets (10-11], (11-12], (12-13]
 */
const generateHourlyRange = (
  startDate: Date,
  stepHours: number,
  endDate: Date,
): Date[] => {
  const totalHours = differenceInHours(endDate, startDate);
  const steps = Math.floor(totalHours / stepHours);
  return Array.from({ length: steps }, (_, i) =>
    addHours(startDate, i * stepHours),
  );
};

// Finds the observations that fall within a given time interval and returns the most recent value
const getIntervalValue = (
  observations: WeatherStationObservation[],
  intervalStart: Date,
  intervalEnd: Date,
  sensorType: WeatherStationObservationDataKey,
): ReturnType<(typeof OBSERVATIONS_DATA_PARSERS)[typeof sensorType]> => {
  const observationsInInterval = observations.filter(
    (obs) =>
      obs.timestamp.getTime() > intervalStart.getTime() &&
      obs.timestamp.getTime() <= intervalEnd.getTime(),
  );

  const latestObservation = getLatestObservation(observationsInInterval);
  const rawValue = latestObservation?.[sensorType];
  return OBSERVATIONS_DATA_PARSERS[sensorType]?.(rawValue);
};

// Builds a data row for a single sensor type across all time intervals.
const buildSensorRow = (
  sensorType: WeatherStationObservationDataKey,
  hourlyDateRange: Date[],
  endAt: Date,
  observations: WeatherStationObservation[],
  focusedSensorType: FocusedSensorType,
): { rowType: 'data'; cells: WeatherStationHistoricalDataCell[] } => {
  const label = OBSERVATION_SENSOR_TYPE_TO_LABEL[sensorType];

  const dataCells = hourlyDateRange.map((bucketStart, index) => {
    const bucketEnd = hourlyDateRange[index + 1] ?? endAt;
    const value = getIntervalValue(
      observations,
      bucketStart,
      bucketEnd,
      sensorType,
    );
    return { type: sensorType, value: value ?? EMPTY_DATA_VALUE };
  });

  const cellValues = dataCells.map((cell) => cell.value);

  const cells: WeatherStationHistoricalDataCell[] = [
    { type: sensorType, value: label },
    ...dataCells.map((cell, index) => ({
      type: cell.type,
      value: cell.value,
      tableCellSx: getTableCellSx({
        values: cellValues,
        type: cell.type,
        value: cell.value,
        index,
        focusedType: focusedSensorType,
      }),
    })),
  ];

  return { rowType: 'data', cells };
};

/**
 * Generates historical weather data for a set of observations, grouped into
 * hourly intervals starting from a given date. The intervals are determined
 * by the specified step (in hours), and the startFrom date is purposely
 * excluded from the resulting range.
 *
 * Each interval contains the most recent observation within that period,
 * parsed and formatted according to the observation value type.
 */
export const getObservationsHistoricalData = (
  observations: WeatherStationObservation[],
  startFrom: Date,
  endAt: Date,
  step: number,
  focusedSensorType: FocusedSensorType,
  isPrecipitationEnabled = false,
): WeatherStationHistoricalData | null => {
  if (observations.length === 0) return null;

  // Array of bucket start times (e.g., [10:00, 11:00, 12:00] for hourly buckets)
  const hourlyDateRange = generateHourlyRange(startFrom, step, endAt);

  if (hourlyDateRange.length === 0) return null;

  const relevantObservations = observations.filter(
    (obs) => obs.timestamp.getTime() >= startFrom.getTime(),
  );

  const head = hourlyDateRange.map((bucketStart) =>
    formatDate(bucketStart, 'h a'),
  );

  const cellCount = hourlyDateRange.length + 1; // +1 for label column

  const sensorTypeGroups = getObservationSensorTypeGroups(
    isPrecipitationEnabled,
  );
  const data = sensorTypeGroups.flatMap((group) => {
    const groupRows = group.map((sensorType) =>
      buildSensorRow(
        sensorType,
        hourlyDateRange,
        endAt,
        relevantObservations,
        focusedSensorType,
      ),
    );
    // Add a separator row after each group
    return [...groupRows, { rowType: 'separator' as const, cellCount }];
  });

  return { head: ['', ...head], data };
};

export const getIndicatorColorForSensorType = (
  sensorType: FocusedSensorType,
  focusedSensorType: FocusedSensorType,
  value: number,
): string | undefined => {
  if (sensorType !== focusedSensorType) {
    return undefined;
  }
  const stops = SENSOR_TYPE_TO_COLOR_STOPS[sensorType];
  return stops ? getInterpolatedColor(value, stops) : undefined;
};

type HourAlignedDateRange = {
  start: Date;
  end: Date;
};

/**
 * Returns hour-aligned start and end dates for a given number of hours.
 *
 * The end time is set to the start of the next hour (inclusive of the current
 * partial hour), and the start time is `hours` before that.
 *
 * This ensures stable bucket boundaries that don't shift as time passes within
 * an hour, which is important for chart displays that poll for updates.
 */
export const getHourAlignedDateRange = (
  hours: number,
  now = new Date(),
): HourAlignedDateRange => {
  const end = addHours(startOfHour(now), 1);
  const start = subHours(end, hours);
  return { start, end };
};
